// The "Drive documents" panel: the documents an agent wrote into Google Drive
// for this project, each opening in Drive where the operator can read, comment
// on and edit it.
//
// It shares GeneratedFilesPanel's stylesheet on purpose. The two sit next to
// each other and list the same kind of thing, so looking different would only
// suggest a difference that isn't there - what separates them is where the file
// lives, which is what the action on each row says.
import styles from "./GeneratedFilesPanel.module.css";

export interface DriveDocument {
  document_id: string;
  filename: string;
  doc_format: string;
  web_view_link: string | null;
  created_by_agent: string;
  updated_at: string;
}

interface Props {
  documents: DriveDocument[];
  // Resolves an agent id to the name an operator recognises.
  agentLabel: (agent: string) => string;
}

const updatedLabel = (iso: string): string => {
  const when = new Date(iso);
  return Number.isNaN(when.getTime())
    ? ""
    : when.toLocaleDateString(undefined, { day: "numeric", month: "short", year: "numeric" });
};

export const DriveDocumentsPanel = ({ documents, agentLabel }: Props) => {
  if (documents.length === 0) return null;

  return (
    <div className={styles.artifacts}>
      <div className={styles.artifactsHead}>Drive documents</div>
      <ul className={styles.artifactList}>
        {documents.map((doc) => (
          <li key={doc.document_id} className={styles.artifactItem}>
            <span className={styles.artifactName}>{doc.filename}</span>
            <span className={styles.artifactAttached}>
              by {agentLabel(doc.created_by_agent)}
              {updatedLabel(doc.updated_at) && `, updated ${updatedLabel(doc.updated_at)}`}
            </span>
            {doc.web_view_link ? (
              <a
                className={styles.downloadBtn}
                href={doc.web_view_link}
                target="_blank"
                rel="noopener noreferrer"
              >
                Open in Drive
              </a>
            ) : (
              // Drive did not return a link for this one. Saying so beats a dead
              // button: the document exists, and the operator can find it in the
              // agents folder.
              <span className={styles.artifactAttached}>no link</span>
            )}
          </li>
        ))}
      </ul>
    </div>
  );
};
